import {
  getConfigByApp,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {COMPANY, COMPANY_CAREER, JOBDETAIL, JOBFEED} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import {Interceptor} from '@jobseeker/common-core/components';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import Company from '@layouts/Body/Company';
import {
  jobDetailService,
  jobfeedService,
} from '@jobseeker/common-core/services';
import type {GetServerSidePropsContext} from 'next/types';
import {AppState} from '@jobseeker/common-core/types';
import {InjectSearchOverlay} from '@components';
import {useEffect} from 'react';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import dynamic from 'next/dynamic';
import {isCSR} from '@jobseeker/lotus/utils';
const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);
type CompanyPageProps = {
  seoConfig: object;
  companyId: string | undefined;
  ambitionBoxData: ABdataProps;
  slug: string | undefined;
  companyJobs: {
    jobs: [];
    hasMore: false;
  };
  companyRefCode?: string;
};

type ABdataProps = {
  ambitionboxCompanyId: number;
  benefits: {
    totalBenefits: number;
    benefits: [
      {
        name: string;
        iconUrl: string;
        count: number;
        totalCount: number;
      },
    ];
    benefitsUrl: string;
    usersReportedBenefits: number;
  };
  companyInfo: {
    ambitionBoxCompanyId: number;
    companyName: string;
    reviewsCount: number;
    companyLogo: string;
    aboutCompany: string;
    foundedYear: string;
    totalEmployees: string;
    totalEmployeesIndia: string;
    companyType: string;
    primaryIndustry: string;
    natureOfBusiness: [string];
    hq: string;
    officeLocations: [
      {
        cityName: string;
      },
    ];
    otherTags: [string];
    title: string;
    url: string;
    aggregateRating: number;
    aggregateRatingOneDecimal: number;
  };
  topJobProfiles: {
    entities: [
      {
        name: string;
        url: string;
        count: number;
        rating: number;
      },
    ];
    topJobProfilesUrl: string;
  };
};

const CompanyPage = ({
  seoConfig,
  ambitionBoxData,
  companyId,
  slug,
  companyJobs,
  companyRefCode,
}: CompanyPageProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBDETAIL);
  const router = useRouter();
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const onLoginSuccess = () => {
    if (isCSR()) {
      window.location.reload();
    }
  };

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: `${ambitionBoxData?.companyInfo?.companyName} Careers`,
      item: `/${slug}-careers`,
    },
  ];

  useValidateAuth(dispatch);
  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );
  const {enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  useEffect(() => {
    TrackEventUBA('companyCareerPageView', 'view', {
      ref: router.query.ref || '',
      filterExp: router.query.minexp
        ? `${router.query.minexp}-${router.query.maxexp}`
        : '',
      filterLocation: router.query.loc || '',
      filterCategory: router.query.cat || '',
      sessionUsername: `${sessionStorage.getItem('userId') || ''}`,
    });
  }, []);
  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout showbottom={true}>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
          <HiristAuthentication
            variant={VersionVariants.VERSION_TWO}
            onSuccess={onLoginSuccess}
            openDialog={enableLoginPopup}
            setOpenDialog={() => {
              dispatch({
                type: 'AppContext/set_login_popup',
                payload: false,
              });
            }}
          />

          <Company
            ambitionBoxData={ambitionBoxData}
            companyId={companyId}
            slug={slug}
            companyJobs={companyJobs}
            companyRefCode={companyRefCode}
          />
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};
export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const url = context?.resolvedUrl || '';

    const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBDETAIL);

    const {
      header: {
        meta: {},
      },
      ads: {learnGrow, featuredInstitutes},
    } = metaData || {};

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: COMPANY},
    });
    let slug = context.params?.companySlug || context.query?.companySlug;
    slug = encodeURIComponent(slug);

    // const ambitionBoxResp = await jobDetailService.getAmbitionBoxData(
    //   slug.split('-').slice(0, -1).join('-')
    // );
    const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
    const userIp = context.req?.headers?.['x-forwarded-for']
      ?.toString()
      .split(',')[0];
    const headers = {
      'user-agent': context.req?.headers?.['user-agent'],
      'x-forwarded-for': userIp,
      referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
    };
    const ambitionBoxResp = await jobDetailService.getAmbitionBoxData(
      slug,
      headers
    );

    const ambitionBoxData = ambitionBoxResp.data;
    const companyId = ambitionBoxData?.centralisedCompanyId || -1;
    const companyRefCode = ambitionBoxData?.data?.refCode ?? '';

    const com_jobs = isAppIimjobs
      ? await jobfeedService.getCompanyJobs(
          slug || '',
          0,
          undefined,
          undefined,
          headers
        )
      : await jobfeedService.getCompanyJobs(
          slug || '',
          0,
          1,
          undefined,
          headers
        );

    const isBotUser = isUserAgentForBot(context);

    if (isBotUser) {
      try {
        const jobFeeds: any = await jobfeedService.getCompanySimilarJobs(
          0,
          headers
        );

        jobFeeds.totalJobs = jobFeeds?.jobs?.length;
        store.dispatch({
          type: 'job/jobFeed/set_jobfeed',
          payload: jobFeeds,
        });
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:200 ~ store jobfeed category jobs=> ~ error:',
          error
        );
      }

      try {
        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: '',
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );
          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: '',
          });

          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ job details Ads API error:',
          error
        );
      }
    }
    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }
    const compName = ambitionBoxData?.companyInfo?.companyName || 'company';
    const seoConfig = await loadSEO(
      COMPANY_CAREER,
      {
        title: `${compName} Careers, ${
          com_jobs?.data?.jobs?.length > 0 ? com_jobs.data.jobs.length : ''
        } ${compName} Jobs, ${new Date().toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
        })}`,
        description: `Apply to ${
          com_jobs?.data?.jobs?.length > 0 ? com_jobs.data.jobs.length : ''
        } ${compName} jobs on ${
          isAppIimjobs ? 'iimjobs.com' : 'Hirist.tech'
        }, India's No.1 ${
          isAppIimjobs ? 'Management' : 'IT'
        } job portal. Explore ${
          isAppIimjobs ? 'Management' : 'IT'
        } job in ${compName} Now!`,
      },
      `/${url?.split('?')[0]?.split('/')[2]}-careers`,
      true
    );
    if (
      !companyId ||
      companyId == '' ||
      companyId == undefined ||
      !ambitionBoxData ||
      Object.keys(ambitionBoxData).length === 0
    ) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        seoConfig,
        metaData,
        companyId: companyId || '',
        ambitionBoxData: ambitionBoxData,
        slug: slug,
        companyJobs: {
          jobs: com_jobs?.data?.jobs,
          hasMore: com_jobs?.data?.hasMore,
        },
        companyRefCode,
      },
    };
  }
);

export default CompanyPage;
